import FormatCurrency from 'components/Common/FormatCurrency'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import FormatPercent from 'components/utils/Formatters/FormatPercent'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import type { ComponentProps } from 'react'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

type InheritedModalProps = Pick<ComponentProps<typeof Modal>, 'primaryActionText'>

interface Props extends InheritedModalProps {
  paymentScheduleTemplate: App.Tours.TourPaymentScheduleTemplate
}

export type TourDepositInfoModalResult = 'primary-action' | undefined

function TourDepositInfoModal({ paymentScheduleTemplate, primaryActionText }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<TourDepositInfoModalResult>()

  const handlePrimaryClick = useCallback(() => {
    modalElement.resolve('primary-action')
  }, [modalElement])

  const deposit = paymentScheduleTemplate.schedules[0]

  const modalTitle = intl.formatMessage({
    id: '89Cz5Mo/RU',
    defaultMessage: 'How it works',
    description: 'Title for the tour deposit information modal',
  })

  return <Modal
    title={modalTitle}
    primaryActionText={primaryActionText}
    onPrimaryActionClick={primaryActionText ? handlePrimaryClick : undefined}
  >
    <Group direction="vertical" gap={16}>
      <div>
        <BodyText variant="medium" colour="neutral-two">
          {/* TODO: i18n - contains leading/trailing spaces */}
          Through Payment Schedules, we provide you flexibility to secure your tour with {' '}
          {deposit.amountType === 'fixed_amount' && <FormatCurrency value={deposit.amountValue} />}
          {deposit.amountType === 'percentage' && <FormatPercent value={deposit.amountValue} />} upfront, and pay off the balance across multiple payments.
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">{intl.formatMessage({
          id: 'M8zNUCbMJd',
          defaultMessage: 'Pay deposit now',
          description: 'Heading for the pay deposit now section in tour deposit modal',
        })}
        </Heading>
        <BodyText variant="medium" colour="neutral-two">
          {/* TODO: i18n - contains leading/trailing spaces */}
          Secure your dates by paying{' '}
          {deposit.amountType === 'fixed_amount' && <FormatCurrency value={deposit.amountValue} />}
          {deposit.amountType === 'percentage' && <FormatPercent value={deposit.amountValue} />}
          {/* TODO: i18n - contains leading/trailing spaces */}
          {' '}of your booking today. Add ons such as  {LUXURY_PLUS.PROGRAM_NAME} memberships, optional experiences, accommodations and travel protection must be paid upfront in addition to the{' '}
          {deposit.amountType === 'fixed_amount' && <FormatCurrency value={deposit.amountValue} />}
          {deposit.amountType === 'percentage' && <FormatPercent value={deposit.amountValue} />}
          {/* TODO: i18n - contains leading/trailing spaces */}
          {' '}deposit.
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">{intl.formatMessage({
          id: 'rCeZVv8XjR',
          defaultMessage: 'Pay the rest later',
          description: 'Heading for the pay later section in tour deposit modal',
        })}
        </Heading>
        <BodyText variant="medium" colour="neutral-two">
          {intl.formatMessage({
            id: 'IwqC7KqN34',
            defaultMessage: 'Payments will be automatically charged to your original payment method on each due date. Don\'t worry, we\'ll send you a reminder email 3 to 5 days before your payment is due.',
            description: 'Body text explaining automatic payment schedule and reminder emails',
          })}
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">{intl.formatMessage({
          id: '0GJhyI55CG',
          defaultMessage: 'Service Fee',
          description: 'Heading for the service fee section in tour deposit modal',
        })}
        </Heading>
        <BodyText variant="medium" colour="neutral-two">
          {intl.formatMessage({
            id: '8HBqP77+IC',
            defaultMessage: 'The service fee paid as part of a Payment Schedule is non-refundable, even if cancellation occurs within 7 days of purchase.',
            description: 'Body text explaining non-refundable service fee policy',
          })}
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">{intl.formatMessage({
          id: 'H861zHNaaD',
          defaultMessage: 'Cancellation policy',
          description: 'Heading for the cancellation policy section in tour deposit modal',
        })}
        </Heading>
        <BodyText variant="medium" colour="neutral-two">
          {intl.formatMessage({
            id: 'YT2G+h+4Wj',
            defaultMessage: 'All deposits, service fee, and payments are non-refundable.',
            description: 'First paragraph of cancellation policy stating non-refundable payments',
          })}
          <br /><br />
          {intl.formatMessage({
            id: 'AzODaWBVos',
            defaultMessage: 'Once you have made a payment as part of your Payment Schedule, that payment is non-refundable. Failure to complete any payment will result in the cancellation of your booking, and you will forfeit the entire amount you have paid.',
            description: 'Second paragraph of cancellation policy about payment forfeiture',
          })}
          <br /><br />
          {intl.formatMessage({
            id: 'JGjEIefukj',
            defaultMessage: 'We may allow a cancellation for credit due to exceptional circumstances (and at our sole discretion). Where a cancellation/credit is not otherwise permitted and you still wish cancel your booking, an additional service fee of A$300 per person will apply (above and beyond the existing fees already paid pursuant to the payment schedules).',
            description: 'Third paragraph of cancellation policy about exceptional circumstances and additional fees',
          })}
        </BodyText>
      </div>
    </Group>
  </Modal>
}

export default TourDepositInfoModal
